import { GraphElementController, MapElementController, VisualController } from '@sas-dvr/ltjs-bird';
import { VisualizationState } from '@sas-dvr/internal-va-react-core/store/birdElement/visualizationState';
import { VisualElementControllerLoader } from './LoaderFactory';
import { loadBreadcrumb } from './loadBreadcrumb';
import { merge, EMPTY, Observable, of } from 'rxjs';
import {
  map,
  filter,
  distinctUntilChanged,
  tap,
  ignoreElements,
  take,
  mergeMap,
} from 'rxjs/operators';
import { ofActionCreatorType } from '../util/rxUtils';
import { fromPortableEvent } from '../util';
import {
  GraphA11yUtil,
  LayoutRegion,
  ControllerOperations,
  SankeyDiagram,
} from '@sas-dvr/ltjs-gtl';
import { VisualizationManager } from '@sas-dvr/ltjs-commons';
import { DragModeState } from '@sas-dvr/internal-va-react-core/store/dragMode/dragModeState';
import { OverviewState } from '@sas-dvr/internal-va-react-core/store/overview/overviewState';
import { getMaximizeObservables } from '../util/maximizeUtils';
import { GraphDescription } from '@sas-dvr/ltjs-gtl';
import { loadVisualization } from './loadVisualization';

export const loadGraphElementController: VisualElementControllerLoader<
  VisualizationManager | undefined
> = (
  visualController,
  visualElementController,
  unload$,
  myActions$,
  state$,
  deps,
  _createElement,
  visualizationManager
) => {
  if (!(visualElementController instanceof GraphElementController)) {
    throw new Error('Expected the controller to be a GraphElementController');
  }
  const visualId = deps.getKey(visualElementController);
  const contentId$ = state$.pipe(
    map((aState) => aState.birdElements.DataVisualizations[visualId]),
    filter(
      (myState): myState is Exclude<Required<typeof myState>, undefined> =>
        myState !== undefined && myState.contentId !== undefined
    ),
    distinctUntilChanged(),
    map((myState) => myState.contentId)
  );

  // Enable drilling on the axis labels
  visualElementController.setCategoryLabelDrillEnabled(true);

  const inComposite =
    visualController instanceof VisualController && visualController.getCompositeParent() !== null;
  const acceleratorEnabled = GraphA11yUtil.hasAccelerator() && !inComposite;

  const loadVisualElement$ = loadVisualization(
    visualController,
    visualElementController,
    unload$,
    myActions$,
    state$,
    deps,
    {
      create: () => VisualizationState.actions.add(visualId, { acceleratorEnabled }),
      delete: () => VisualizationState.actions.delete(visualId),
    },
    { contentId$, visualizationManager, enableScrollOverlay: true }
  );

  const loadVisualBreadcrumb$ = loadBreadcrumb(
    visualElementController,
    unload$,
    myActions$,
    state$,
    visualId
  );

  const handleGraphAccelerator$ = myActions$.pipe(
    ofActionCreatorType<typeof VisualizationState.actions.openGraphAccelerator>(
      VisualizationState.actionTypes.openGraphAccelerator
    ),
    tap(() => {
      GraphA11yUtil.notifyAccelerator(
        visualElementController.getGraphModel(),
        visualElementController.getAccessibilityLabel() ?? '',
        ''
      );
    }),
    ignoreElements()
  );

  // Emits a single arbitrary value when controller operations are ready
  let operationsReady$: Observable<unknown>;
  {
    if (visualElementController.getAvailableDragModes()?.length) {
      // If drag modes are available, operations must be ready
      operationsReady$ = of(null);
    } else if (visualElementController instanceof MapElementController) {
      // Maps listen for CONTROLLER_EVENT_TYPE on the Layout and bubble the event
      operationsReady$ = visualElementController.hasOperations()
        ? of(null)
        : fromPortableEvent(visualElementController, ControllerOperations.CONTROLLER_EVENT_TYPE);
    } else {
      // For all others, listen for CONTROLLER_EVENT_TYPE on the graph model
      const graph = visualElementController.getGraphModel();
      operationsReady$ = graph
        ? fromPortableEvent(graph, ControllerOperations.CONTROLLER_EVENT_TYPE)
        : EMPTY;
    }
    // emit at most one value
    operationsReady$ = operationsReady$.pipe(take(1));
  }

  const loadDragMode$ = operationsReady$.pipe(
    mergeMap(() => {
      const graph = visualElementController.getGraphModel();
      const layout = graph?.getLayout();
      const region = layout instanceof LayoutRegion ? layout.getRegion() : null;
      const isSankeyDiagram = region instanceof SankeyDiagram;
      const isMap = visualElementController instanceof MapElementController;
      // Other graphs return values for getAvailableDragModes, but only maps and sankey diagrams show the UI.
      // Network diagrams are included in isMap.
      if (!isMap && !isSankeyDiagram) {
        return EMPTY;
      }
      const availableModes = visualElementController.getAvailableDragModes()?.sort() ?? [];
      const currentMode = visualElementController.getDragMode();
      return merge(
        of(DragModeState.actions.init(visualId, { currentMode, availableModes })),
        myActions$.pipe(
          ofActionCreatorType<typeof DragModeState.actions.setCurrentMode>(
            DragModeState.actionTypes.setCurrentMode
          ),
          tap((a) => {
            visualElementController.setDragMode(a.payload.data);
          }),
          ignoreElements()
        )
      );
    })
  );

  const loadOverview$ = operationsReady$.pipe(
    mergeMap(() => {
      if (!visualElementController.isOverviewSupported()) {
        return EMPTY;
      }
      const isOverviewOn = visualElementController.isOverviewOn();
      return merge(
        of(OverviewState.actions.set(visualId, { isOverviewOn })),
        myActions$.pipe(
          ofActionCreatorType<typeof OverviewState.actions.toggleOverview>(
            OverviewState.actionTypes.toggleOverview
          ),
          tap(() => {
            visualElementController.toggleOverview();
          }),
          map(() =>
            OverviewState.actions.setIsOverviewOn(visualId, visualElementController.isOverviewOn())
          )
        )
      );
    })
  );

  const handleMaximize$ = getMaximizeObservables(
    visualId,
    visualController,
    visualElementController,
    state$,
    unload$,
    {
      preferOverflowMenu:
        visualElementController.getVisualElementTypeInfo()?.getGraphDescription() ===
        GraphDescription.KEY_VALUE,
    }
  );

  return merge(
    loadVisualElement$,
    loadVisualBreadcrumb$,
    handleGraphAccelerator$,
    loadDragMode$,
    loadOverview$,
    handleMaximize$
  );
};
